import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  Settings, 
  Play, 
  FileSpreadsheet, 
  CheckSquare, 
  Download,
  Trash2,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { evaluationsApi, versionsApi } from '../services/api';

interface NotificationFunction {
  (message: string, type: 'success' | 'error' | 'info' | 'warning', duration?: number): void;
}

interface EvaluationsProps {
  addNotification: NotificationFunction;
}

interface Metric {
  value: string;
  name: string;
  description: string;
  family?: string;
  required_fields?: string[];
}

interface EvaluationRun {
  id: number;
  file_name: string;
  total_rows: number;
  processed_rows: number;
  status: string;
  selected_metrics: string[];
  llm_model: string;
  created_at: string;
  completed_at?: string;
}

const Evaluations: React.FC<EvaluationsProps> = ({ addNotification }) => {
  const [currentStep, setCurrentStep] = useState<'upload' | 'map' | 'configure' | 'execute' | 'results'>('upload');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [availableMetrics, setAvailableMetrics] = useState<Metric[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('gpt-4o-mini');
  const [evaluationRunId, setEvaluationRunId] = useState<number | null>(null);
  const [evaluationRuns, setEvaluationRuns] = useState<EvaluationRun[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadValidation, setUploadValidation] = useState<any>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string | null>>({});

  const canonicalFieldLabels: Record<string, string> = {
    question: 'Question',
    answer: 'Answer',
    context: 'Context',
    expected_answer: 'Expected Answer',
    conversation_id: 'Conversation ID',
    model: 'Model',
    tool_trace_json: 'Tool Trace JSON',
    tool_count: 'Tool Count',
    source_count: 'Source Count',
    sources: 'Sources',
    task_details: 'Task Details',
    reasoning_trace: 'Reasoning Trace',
    thinking_tokens: 'Thinking Tokens',
    messages: 'Messages',
    turn_count: 'Turn Count',
    turn_metadata: 'Turn Metadata',
  };

  const profileLabels: Record<string, string> = {
    rag: 'RAG',
    agentic: 'Agentic',
    reasoning: 'Reasoning',
    rag_agentic: 'RAG + Agentic',
    rag_reasoning: 'RAG + Reasoning',
    agentic_reasoning: 'Agentic + Reasoning',
    rag_agentic_reasoning: 'RAG + Agentic + Reasoning',
    qa: 'General Q&A',
    error: 'Error Response',
    unknown: 'Unknown',
  };

  const metricNameByValue = (value: string) => {
    return availableMetrics.find((metric) => metric.value === value)?.name || value.replace(/_/g, ' ');
  };

  const applySuggestedMetrics = (metrics: string[]) => {
    const knownMetricValues = new Set(availableMetrics.map((metric) => metric.value));
    setSelectedMetrics(metrics.filter((metric) => knownMetricValues.has(metric)));
  };

  const getEvaluationPlan = () => {
    const rows = uploadValidation?.classification_summary?.rows || [];
    const selectedMetricValues = new Set(selectedMetrics);
    const byProfile: Record<string, {
      rows: number;
      rowsWithMetrics: number;
      metricEvaluations: number;
      metrics: Set<string>;
    }> = {};

    rows.forEach((row: any) => {
      const profile = row.evaluation_profile || 'unknown';
      if (!byProfile[profile]) {
        byProfile[profile] = {
          rows: 0,
          rowsWithMetrics: 0,
          metricEvaluations: 0,
          metrics: new Set<string>(),
        };
      }

      const applicableMetrics = (row.suggested_metrics || []).filter((metric: string) => selectedMetricValues.has(metric));
      byProfile[profile].rows += 1;
      byProfile[profile].metricEvaluations += applicableMetrics.length;

      if (applicableMetrics.length > 0) {
        byProfile[profile].rowsWithMetrics += 1;
        applicableMetrics.forEach((metric: string) => byProfile[profile].metrics.add(metric));
      }
    });

    const plannedRows = Object.values(byProfile).reduce((total, profile) => total + profile.rowsWithMetrics, 0);
    const plannedMetricEvaluations = Object.values(byProfile).reduce((total, profile) => total + profile.metricEvaluations, 0);
    const totalRows = rows.length || uploadValidation?.total_rows || 0;

    return {
      byProfile,
      plannedRows,
      skippedRows: Math.max(totalRows - plannedRows, 0),
      plannedMetricEvaluations,
      hasRowPlan: rows.length > 0,
    };
  };

  // Load available metrics and existing runs on component mount
  useEffect(() => {
    loadAvailableMetrics();
    
    const orgIdStr = localStorage.getItem('selectedOrganizationId');
    const projectIdStr = localStorage.getItem('selectedProjectId');
    const orgId = orgIdStr ? parseInt(orgIdStr) : null;
    const projectId = projectIdStr ? parseInt(projectIdStr) : null;
    setSelectedOrganizationId(orgId);
    setSelectedProjectId(projectId);
    
    const handleOrganizationChange = () => {
      const newOrgIdStr = localStorage.getItem('selectedOrganizationId');
      const newProjectIdStr = localStorage.getItem('selectedProjectId');
      const newOrgId = newOrgIdStr ? parseInt(newOrgIdStr) : null;
      const newProjectId = newProjectIdStr ? parseInt(newProjectIdStr) : null;
      setSelectedOrganizationId(newOrgId);
      setSelectedProjectId(newProjectId);
    };
    
    const handleProjectChange = () => {
      const newProjectIdStr = localStorage.getItem('selectedProjectId');
      const newProjectId = newProjectIdStr ? parseInt(newProjectIdStr) : null;
      setSelectedProjectId(newProjectId);
    };
    
    window.addEventListener('organizationChanged', handleOrganizationChange);
    window.addEventListener('projectChanged', handleProjectChange);
    
    return () => {
      window.removeEventListener('organizationChanged', handleOrganizationChange);
      window.removeEventListener('projectChanged', handleProjectChange);
    };
  }, []);
  
  // Load versions when organization or project changes
  useEffect(() => {
    if (selectedOrganizationId && selectedProjectId) {
      loadVersions(selectedOrganizationId);
    } else {
      setVersions([]);
      setSelectedVersionId(null);
    }
  }, [selectedOrganizationId, selectedProjectId]);

  // Load versions when selectedVersionId changes
  useEffect(() => {
    if (selectedOrganizationId && selectedProjectId && selectedVersionId) {
      loadEvaluationRuns();
    }
  }, [selectedOrganizationId, selectedProjectId, selectedVersionId]);

  const loadAvailableMetrics = async () => {
    try {
      const data = await evaluationsApi.getMetrics();
      if (data.success) {
        setAvailableMetrics(data.metrics);
      }
    } catch (error) {
      console.error('Error loading metrics:', error);
      addNotification('Failed to load available metrics', 'error');
    }
  };

  const loadVersions = async (organizationId: number) => {
    try {
      if (!selectedProjectId) { 
        setVersions([]);
        setSelectedVersionId(null);
        return;
      }
      const data = await versionsApi.list(organizationId, selectedProjectId);
      setVersions(data);
      // Auto-select first version if none selected or current selection doesn't exist
      if (data.length > 0) {
        const currentVersionExists = data.some(v => v.id === selectedVersionId);
        if (!currentVersionExists) {
          setSelectedVersionId(data[0].id);
        }
      } else {
        setSelectedVersionId(null);
      }
    } catch (error) {
      console.error('Error loading versions:', error);
      addNotification('Failed to load versions', 'error');
      setVersions([]);
      setSelectedVersionId(null);
    }
  };

  const loadEvaluationRuns = async () => {
    if (!selectedOrganizationId || !selectedProjectId || !selectedVersionId) {
      setEvaluationRuns([]);
      return;
    }
    
    try {
      const data = await evaluationsApi.getEvaluationRuns(selectedOrganizationId, selectedProjectId, selectedVersionId, 1, 10);
      if (data.evaluation_runs) {
        setEvaluationRuns(data.evaluation_runs);
      }
    } catch (error) {
      console.error('Error loading evaluation runs:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedOrganizationId || !selectedProjectId || !selectedVersionId) {
      addNotification('Please select an organization, project and version', 'error');
      return;
    }

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv') && !file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      addNotification('Please upload a CSV or Excel file (.csv, .xlsx, .xls)', 'error');
      return;
    }

    setUploadedFile(file);
    setIsProcessing(true);

    try {
      const data = await evaluationsApi.uploadExcel(selectedOrganizationId, selectedProjectId, selectedVersionId, file);

      if (data.success && data.evaluation_run_id) {
        setUploadValidation(data);
        setEvaluationRunId(data.evaluation_run_id);
        setColumnMapping(data.suggested_column_mapping || {});
        if (data.classification_summary?.suggested_metrics) {
          applySuggestedMetrics(data.classification_summary.suggested_metrics);
        }
        setCurrentStep('map');
        addNotification('File uploaded successfully. Confirm column mappings to continue.', 'success');
      } else {
        addNotification(`Upload failed: ${data.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      addNotification('Failed to upload file', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmColumnMapping = async () => {
    if (!evaluationRunId) return;

    if (!selectedOrganizationId || !selectedProjectId || !selectedVersionId) {
      addNotification('Please select an organization, project and version', 'error');
      return;
    }

    const requiredFields = uploadValidation?.required_fields || ['question', 'answer'];
    const missingRequired = requiredFields.filter((field: string) => !columnMapping[field]);
    if (missingRequired.length > 0) {
      addNotification(`Map required fields: ${missingRequired.map((field: string) => canonicalFieldLabels[field] || field).join(', ')}`, 'error');
      return;
    }

    setIsProcessing(true);
    try {
      const data = await evaluationsApi.configureColumnMapping(selectedOrganizationId, selectedProjectId, selectedVersionId, {
        evaluation_run_id: evaluationRunId,
        column_mapping: columnMapping,
      });

      if (data.success) {
        if (data.evaluation_run_id) {
          setEvaluationRunId(data.evaluation_run_id);
        }
        setUploadValidation((prev: any) => ({ ...prev, ...data }));
        applySuggestedMetrics(data.suggested_metrics || data.classification_summary?.suggested_metrics || []);
        setCurrentStep('configure');
        addNotification('Column mapping saved successfully', 'success');
      } else {
        addNotification(`Column mapping failed: ${data.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Column mapping error:', error);
      addNotification('Failed to configure column mapping', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfigureEvaluation = async () => {
    if (!evaluationRunId || selectedMetrics.length === 0) {
      addNotification('Please select at least one metric and provide a model', 'warning');
      return;
    }

    if (!selectedOrganizationId || !selectedProjectId || !selectedVersionId) {
      addNotification('Please select an organization, project and version', 'error');
      return;
    }

    setIsProcessing(true);

    try {
      const data = await evaluationsApi.configureEvaluation(selectedOrganizationId, selectedProjectId, selectedVersionId, {
        evaluation_run_id: evaluationRunId,
        selected_metrics: selectedMetrics,
        llm_model: selectedModel,
      });

      if (data.success) {
        if (data.evaluation_run_id) {
          setEvaluationRunId(data.evaluation_run_id);
        }
        setCurrentStep('execute');
        addNotification('Evaluation configuration saved successfully', 'success');
      } else {
        addNotification(`Configuration failed: ${data.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Configuration error:', error);
      addNotification('Failed to configure evaluation', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRunEvaluation = async () => {
    if (!evaluationRunId) return;

    if (!selectedOrganizationId || !selectedProjectId || !selectedVersionId) {
      addNotification('Please select an organization, project and version', 'error');
      return;
    }

    setIsProcessing(true);

    try {
      const data = await evaluationsApi.runEvaluation(selectedOrganizationId, selectedProjectId, selectedVersionId, evaluationRunId);

      if (data.success) {
        if (data.evaluation_run_id) {
          setEvaluationRunId(data.evaluation_run_id);
        }
        setCurrentStep('results');
        addNotification(`Evaluation completed successfully. Evaluation ID: ${data.evaluation_run_id || evaluationRunId}`, 'success');
        loadEvaluationRuns(); // Refresh the list
      } else {
        addNotification(`Evaluation failed: ${data.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Evaluation error:', error);
      const message = (error as any)?.response?.data?.detail || 'Failed to run evaluation';
      addNotification(message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteRun = async (runId: number) => {
    if (!window.confirm('Are you sure you want to delete this evaluation run?')) {
      return;
    }

    if (!selectedOrganizationId || !selectedProjectId || !selectedVersionId) {
      addNotification('Please select an organization, project and version', 'error');
      return;
    }

    try {
      const data = await evaluationsApi.deleteEvaluationRun(selectedOrganizationId, selectedProjectId, selectedVersionId, runId);

      if (data.success) {
        addNotification('Evaluation run deleted successfully', 'success');
        loadEvaluationRuns();
      } else {
        addNotification(`Delete failed: ${data.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      addNotification('Failed to delete evaluation run', 'error');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-slate-100">Upload Evaluation File</h3>
        <p className="mt-1 text-sm text-slate-400">
          Upload CSV or Excel data, then map its columns to evaluation fields
        </p>
      </div>

      <div className="mt-6">
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-slate-600 border-dashed rounded-md">
          <div className="space-y-1 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <div className="flex text-sm text-slate-300">
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer bg-slate-800 rounded-md border border-slate-700 font-medium text-purple-500 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
              >
                <span>Upload a file</span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  disabled={isProcessing}
                />
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-slate-400">CSV or Excel files up to 10MB</p>
          </div>
        </div>
      </div>

      {uploadedFile && (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FileSpreadsheet className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-slate-100">
                File Uploaded: {uploadedFile.name}
              </h3>
              <div className="mt-2 text-sm text-slate-300">
                <p>File size: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderMappingStep = () => {
    const columns: string[] = uploadValidation?.columns || [];
    const fields: string[] = uploadValidation?.canonical_fields || Object.keys(canonicalFieldLabels);
    const requiredFields: string[] = uploadValidation?.required_fields || ['question', 'answer'];

    return (
      <div className="space-y-6">
        <div className="text-center">
          <Settings className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Map Columns</h3>
          <p className="mt-1 text-sm text-gray-500">
            Match uploaded columns to the fields needed for RAG and Agentic evaluation
          </p>
        </div>

        {uploadValidation && (
          <div className="bg-slate-900 border border-slate-700 rounded-md p-4">
            <h4 className="text-sm font-medium text-slate-100">File Parsed Successfully</h4>
            <div className="mt-2 text-sm text-slate-300">
              <p>Total rows: {uploadValidation.total_rows}</p>
              <p>Non-empty rows: {uploadValidation.valid_rows}</p>
              <p>Columns found: {columns.length}</p>
            </div>
          </div>
        )}

        {uploadValidation?.classification_summary && (
          <div className="bg-slate-900 border border-slate-700 rounded-md p-4">
            <h4 className="text-sm font-medium text-slate-100">Intelligent Classification Preview</h4>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-2">Detected row types</p>
                <div className="space-y-1">
                  {Object.entries(uploadValidation.classification_summary.profiles || {}).map(([profile, count]) => (
                    <div key={profile} className="flex justify-between text-sm">
                      <span className="text-slate-300">{profileLabels[profile] || profile}</span>
                      <span className="text-slate-100 font-medium">{String(count)}</span>
                    </div>
                  ))}
                </div>
                {uploadValidation.classification_summary.multiturn && (
                  <p className="mt-2 text-xs text-slate-400">
                    Multiturn rows: {uploadValidation.classification_summary.multiturn.true || 0}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-2">Suggested metrics</p>
                <div className="flex flex-wrap gap-2">
                  {(uploadValidation.classification_summary.suggested_metrics || []).map((metric: string) => (
                    <span key={metric} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-900/50 text-purple-200">
                      {metricNameByValue(metric)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            {uploadValidation.classification_summary.sampled_rows && (
              <p className="mt-3 text-xs text-slate-400">
                Preview based on first {uploadValidation.classification_summary.sampled_rows} row(s). Confirm mappings to classify all rows.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((field) => {
            const isRequired = requiredFields.includes(field);
            return (
              <div key={field}>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  {canonicalFieldLabels[field] || field}
                  {isRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
                <select
                  value={columnMapping[field] || ''}
                  onChange={(e) => setColumnMapping({ ...columnMapping, [field]: e.target.value || null })}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Not mapped</option>
                  {columns.map((column) => (
                    <option key={`${field}-${column}`} value={column}>{column}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        {uploadValidation?.sample_data?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-slate-200 mb-2">Sample Rows</h4>
            <div className="overflow-x-auto border border-slate-700 rounded-md">
              <table className="min-w-full divide-y divide-slate-700 text-xs">
                <thead className="bg-slate-900">
                  <tr>
                    {columns.map((column) => (
                      <th key={column} className="px-3 py-2 text-left font-medium text-slate-300">{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {uploadValidation.sample_data.map((row: any, index: number) => (
                    <tr key={index}>
                      {columns.map((column) => (
                        <td key={`${index}-${column}`} className="px-3 py-2 text-slate-300 max-w-xs truncate">
                          {row[column] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setCurrentStep('upload')}
            className="px-4 py-2 border border-slate-600 rounded-md text-sm font-medium text-slate-200 bg-slate-800 hover:bg-slate-700"
          >
            Back
          </button>
          <button
            onClick={handleConfirmColumnMapping}
            disabled={isProcessing}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {isProcessing ? 'Saving Mapping...' : 'Continue'}
          </button>
        </div>
      </div>
    );
  };

  const renderConfigureStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Settings className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-slate-100">Configure Evaluation</h3>
        <p className="mt-1 text-sm text-slate-400">
          Select RAG and Agentic metrics plus the LLM judge model
        </p>
      </div>

      {uploadValidation && (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-4">
          <h4 className="text-sm font-medium text-slate-100">File Validation Results</h4>
          <div className="mt-2 text-sm text-slate-300">
            <p>Total rows: {uploadValidation.total_rows}</p>
            <p>Valid rows: {uploadValidation.valid_rows}</p>
            <p>Empty rows: {uploadValidation.empty_rows}</p>
          </div>
        </div>
      )}

      {uploadValidation?.classification_summary && (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-medium text-slate-100">Intelligent Metric Suggestions</h4>
              <p className="mt-1 text-sm text-slate-400">
                Metrics were suggested from row profiles. The backend applies selected metrics only to rows where that metric fits the detected profile.
              </p>
            </div>
            <button
              onClick={() => applySuggestedMetrics(uploadValidation.classification_summary.suggested_metrics || [])}
              className="px-3 py-2 border border-slate-600 rounded-md text-xs font-medium text-slate-200 bg-slate-800 hover:bg-slate-700"
            >
              Reapply Suggestions
            </button>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-2">Rows by profile</p>
              <div className="space-y-1">
                {Object.entries(uploadValidation.classification_summary.profiles || {}).map(([profile, count]) => (
                  <div key={profile} className="flex justify-between text-sm">
                    <span className="text-slate-300">{profileLabels[profile] || profile}</span>
                    <span className="text-slate-100 font-medium">{String(count)}</span>
                  </div>
                ))}
              </div>
              {uploadValidation.classification_summary.multiturn && (
                <p className="mt-2 text-xs text-slate-400">
                  Multiturn rows: {uploadValidation.classification_summary.multiturn.true || 0} / {uploadValidation.total_rows}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-2">Suggested metric pool</p>
              <div className="flex flex-wrap gap-2">
                {(uploadValidation.classification_summary.suggested_metrics || []).map((metric: string) => (
                  <span key={metric} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-900/50 text-purple-200">
                    {metricNameByValue(metric)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-200 mb-2">
            Select Evaluation Metrics
          </label>
          <div className="space-y-2">
            {availableMetrics.map((metric) => {
              const missingFields = (metric.required_fields || []).filter((field) => !columnMapping[field]);
              return (
                <label key={metric.value} className="flex items-start">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-purple-500 focus:ring-indigo-500 border-slate-600 rounded"
                    checked={selectedMetrics.includes(metric.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedMetrics([...selectedMetrics, metric.value]);
                      } else {
                        setSelectedMetrics(selectedMetrics.filter(m => m !== metric.value));
                      }
                    }}
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-slate-100">
                      {metric.name}
                      {metric.family === 'agentic' && <span className="ml-2 text-xs text-purple-500">(Agentic)</span>}
                    </div>
                    <div className="text-sm text-slate-300">{metric.description}</div>
                    {missingFields.length > 0 && (
                      <div className="text-xs text-yellow-400">
                        Needs mapping: {missingFields.map((field) => canonicalFieldLabels[field] || field).join(', ')}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-200 mb-2">
            LLM Model
          </label>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base bg-slate-900 text-slate-100 border border-slate-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="gpt-4o-mini">GPT-4o Mini</option>
            <option value="gpt-4o">GPT-4o</option>
            <option value="gpt-4">GPT-4</option>
            <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          onClick={() => setCurrentStep('map')}
          className="px-4 py-2 border border-slate-600 rounded-md text-sm font-medium text-slate-200 bg-slate-800 hover:bg-slate-700"
        >
          Back
        </button>
        <button
          onClick={handleConfigureEvaluation}
          disabled={isProcessing || selectedMetrics.length === 0}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
        >
          {isProcessing ? 'Configuring...' : 'Continue'}
        </button>
      </div>
    </div>
  );

  const renderExecuteStep = () => {
    const evaluationPlan = getEvaluationPlan();
    const profilePlanEntries = Object.entries(evaluationPlan.byProfile);

    return (
      <div className="space-y-6">
        <div className="text-center">
          <Play className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-slate-100">Run Evaluation</h3>
          <p className="mt-1 text-sm text-slate-400">
            Execute selected evaluations for normalized rows by classification
          </p>
        </div>

        <div className="bg-slate-900 border border-slate-700 rounded-md p-4">
          <h4 className="text-sm font-medium text-slate-100">Evaluation Configuration</h4>
          <div className="mt-2 text-sm text-slate-300 space-y-1">
            <p>File: {uploadedFile?.name}</p>
            <p>Evaluation ID: {evaluationRunId}</p>
            <p>Total rows: {uploadValidation?.total_rows}</p>
            <p>Rows with planned metric evaluations: {evaluationPlan.plannedRows}</p>
            <p>Rows skipped: {evaluationPlan.skippedRows}</p>
            <p>Selected metric pool: {selectedMetrics.length}</p>
            <p>Estimated metric evaluations: {evaluationPlan.plannedMetricEvaluations}</p>
            <p>LLM Model: {selectedModel}</p>
          </div>

          <div className="mt-4 rounded-md border border-slate-700 bg-slate-950/60 p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h5 className="text-sm font-medium text-slate-100">Planned Evaluations By Classification</h5>
                <p className="mt-1 text-xs text-slate-400">
                  The selected metric pool is intersected with each row classification, so metrics run only where applicable.
                </p>
              </div>
            </div>

            {evaluationPlan.hasRowPlan && profilePlanEntries.length > 0 ? (
              <div className="mt-3 space-y-2">
                {profilePlanEntries.map(([profile, plan]) => (
                  <div key={profile} className="rounded-md border border-slate-700 bg-slate-900 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <span className="text-sm font-medium text-slate-100">{profileLabels[profile] || profile}</span>
                        <span className="ml-2 text-xs text-slate-400">
                          {plan.rows} row{plan.rows === 1 ? '' : 's'}
                        </span>
                      </div>
                      <span className="text-xs font-medium text-slate-300">
                        {plan.metricEvaluations} planned metric eval{plan.metricEvaluations === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-slate-400">
                      Rows with applicable metrics: {plan.rowsWithMetrics} / {plan.rows}
                    </div>
                    {plan.metrics.size > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Array.from(plan.metrics).map((metric) => (
                          <span key={metric} className="inline-flex items-center rounded-full bg-purple-900/50 px-2 py-1 text-xs text-purple-200">
                            {metricNameByValue(metric)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-yellow-400">No selected metrics apply to this classification.</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-400">
                Confirm column mapping to calculate the row-level evaluation plan.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setCurrentStep('configure')}
            className="px-4 py-2 border border-slate-600 rounded-md text-sm font-medium text-slate-200 bg-slate-800 hover:bg-slate-700"
          >
            Back
          </button>
          <button
            onClick={handleRunEvaluation}
            disabled={isProcessing}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
          >
            {isProcessing ? 'Running Evaluation...' : 'Run Evaluation'}
          </button>
        </div>
      </div>
    );
  };

  const renderResultsStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <CheckSquare className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Evaluation Complete</h3>
        <p className="mt-1 text-sm text-gray-500">
          Your evaluation has been completed successfully
        </p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-md p-4">
        <h4 className="text-sm font-medium text-green-800">Evaluation Results</h4>
        <div className="mt-2 text-sm text-green-700">
          <p>File: {uploadedFile?.name}</p>
          <p>Total evaluations completed</p>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          onClick={() => {
            setCurrentStep('upload');
            setUploadedFile(null);
            setEvaluationRunId(null);
            setSelectedMetrics([]);
            setUploadValidation(null);
            setColumnMapping({});
          }}
          className="px-4 py-2 border border-slate-600 rounded-md text-sm font-medium text-slate-200 bg-slate-800 hover:bg-slate-700"
        >
          Start New Evaluation
        </button>
        <button
          onClick={() => setCurrentStep('upload')}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          View Results
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Response Evaluations</h1>
        <p className="mt-2 text-slate-300">
          Upload CSV or Excel files and run RAG plus Agentic evaluations using LLM judges
        </p>
      </div>

      {/* Version Selector */}
      {selectedOrganizationId && selectedProjectId ? (
        <div className="mb-6 bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Version *
            </label>
            <select
              value={selectedVersionId || ''}
              onChange={(e) => setSelectedVersionId(Number(e.target.value))}
              className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              disabled={versions.length === 0}
            >
              <option value="">
                {versions.length === 0 ? 'No versions available' : 'Select a version'}
              </option>
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  {version.version_name} {version.is_active && '(Active)'}
                </option>
              ))}
            </select>
            {!selectedVersionId && versions.length > 0 && (
              <p className="mt-1 text-sm text-red-600">
                Please select a version to continue
              </p>
            )}
            {versions.length === 0 && (
              <p className="mt-1 text-sm text-yellow-600">
                No versions found for this project. Please create a version in the Versions page.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
            <div className="text-sm text-yellow-700">
              <p className="font-medium">Organization and Project Required</p>
              <p className="mt-1">
                Please select an organization and project from the Organizations page to continue with evaluations.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Step Indicator */}
      <div className="mb-8">
        <nav className="flex items-center justify-center" aria-label="Progress">
          <ol className="flex items-center space-x-5">
            {[
              { key: 'upload', label: 'Upload', icon: Upload },
              { key: 'map', label: 'Map', icon: Settings },
              { key: 'configure', label: 'Configure', icon: Settings },
              { key: 'execute', label: 'Execute', icon: Play },
              { key: 'results', label: 'Results', icon: CheckSquare },
            ].map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.key;
              const stepOrder = ['upload', 'map', 'configure', 'execute', 'results'];
              const isCompleted = stepOrder.indexOf(currentStep) > index;
              
              return (
                <li key={step.key} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    isActive ? 'bg-indigo-600 text-white' :
                    isCompleted ? 'bg-green-600 text-white' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    <StepIcon className="w-5 h-5" />
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    isActive ? 'text-purple-500' : 'text-gray-500'
                  }`}>
                    {step.label}
                  </span>
                  {index < 4 && (
                    <div className="ml-5 w-20 h-0.5 bg-gray-200" />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>

      {/* Main Content */}
      <div className="bg-slate-800 shadow rounded-lg border border-slate-700">
        <div className="px-4 py-5 sm:p-6">
          {currentStep === 'upload' && renderUploadStep()}
          {currentStep === 'map' && renderMappingStep()}
          {currentStep === 'configure' && renderConfigureStep()}
          {currentStep === 'execute' && renderExecuteStep()}
          {currentStep === 'results' && renderResultsStep()}
        </div>
      </div>

      {/* Existing Evaluation Runs */}
      {evaluationRuns.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-bold text-slate-100 mb-4">Previous Evaluations</h2>
          <div className="bg-slate-800 shadow rounded-lg border border-slate-700 overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-700">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Eval ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        File Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Progress
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Model
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-slate-800 divide-y divide-slate-700">
                    {evaluationRuns.map((run) => (
                      <tr key={run.id} className="hover:bg-slate-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-100">
                          #{run.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-100">
                          {run.file_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(run.status)}
                            <span className="ml-2 text-sm text-slate-100 capitalize">
                              {run.status.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-100">
                          {run.processed_rows} / {run.total_rows}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-100">
                          {run.llm_model}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                          {new Date(run.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button className="text-purple-500 hover:text-indigo-900">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="text-red-600 hover:text-red-900" onClick={() => handleDeleteRun(run.id)}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Evaluations;
